// Agents (FD §14, §22.2) — an agent is a role put to work: a role, an
// executor and model, in a project. List plus a create/edit drawer.

import { useEffect, useState, type FormEvent } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Link } from '@tanstack/react-router'
import {
  AgentCreateSchema,
  SANDBOX_PROFILES,
  SANDBOX_PROFILE_TYPES,
  safeParseRoleRunSettings,
  type Agent,
  type RunSettingsOverride,
  type SandboxProfileType,
  type AgentSkillOverride,
} from '@goblic/shared'
import {
  agentsApi,
  mcpServicesApi,
  rolesApi,
  skillsApi,
} from '../lib/api-client'
import { StatusBadge } from '../components/status-badge'
import { ExhaustionBadge } from '../components/exhaustion-badge'
import { exhaustionBadge } from '../lib/run-recovery'
import {
  Drawer,
  EmptyState,
  Field,
  LoadingRows,
  Notice,
  PageHeader,
} from '../components/ui'
import { useWorkspaceId } from '../lib/auth'
import { useLookups } from '../hooks/useLookups'
import { useDialogs } from '../components/dialogs'
import { formatRunCost, formatMs } from '../lib/format'
import { RoleAvatar } from '../components/goblin-art'
import { ModelPicker } from '../components/model-picker'
import { RunSettingsOverrides } from '../components/run-settings-overrides'
import { CONNECTION_KIND_LABEL, type ConnectionKind, type RunnerKind } from '../lib/api-client'

/** Human names for the runners a connection can pick between (FD:
 * docs/plans/2026-09-30-runners/03-pi-runner.md §2.6). `pi` is the
 * lightweight runner behind an API key or OpenAI-compatible connection —
 * an implementation detail, so it's never named as "Pi" in the UI. */
const RUNNER_LABEL: Record<RunnerKind, string> = {
  'claude-cli': 'Claude Code',
  'codex-cli': 'Codex',
  pi: 'Lightweight runner',
}

/** The project's Agents section (/p/:key/agents): only its agents, created into it. */
export function AgentsPage({ projectId }: { projectId: string }) {
  const workspaceId = useWorkspaceId()
  const lookups = useLookups()
  const queryClient = useQueryClient()
  const [editing, setEditing] = useState<Agent | 'new' | null>(null)
  const [showPaused, setShowPaused] = useState(false)
  const { confirm, alert } = useDialogs()

  const removeMutation = useMutation({
    mutationFn: (agent: Agent) => agentsApi.remove(agent.id, workspaceId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['agents', workspaceId] })
      setEditing(null)
    },
    onError: (error) =>
      void alert({ kind: 'error', title: 'Couldn’t delete the agent', message: (error as Error).message }),
  })

  async function handleDelete(agent: Agent): Promise<void> {
    const ok = await confirm({
      kind: 'delete',
      title: `Delete ${agent.name} permanently?`,
      message: 'This can’t be undone. An agent that already has runs can’t be deleted; pause it instead.',
      confirmLabel: 'Delete agent',
    })
    if (ok) removeMutation.mutate(agent)
  }

  const saveMutation = useMutation({
    mutationFn: (input: Partial<Agent>) =>
      editing && editing !== 'new'
        ? agentsApi.update(editing.id, workspaceId, input)
        : agentsApi.create({ workspaceId, ...input }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['agents', workspaceId] })
      setEditing(null)
    },
  })

  function close(): void {
    saveMutation.reset()
    setEditing(null)
  }

  const allAgents = projectId ? lookups.agents.filter((a) => a.projectId === projectId) : lookups.agents
  const pausedCount = allAgents.filter((a) => !a.isActive).length
  const agents = showPaused ? allAgents : allAgents.filter((a) => a.isActive)

  return (
    <div>
      <PageHeader
        title="Agents"
        description={
          projectId
            ? `The agents of ${lookups.projectName(projectId)}: each combines a role with an executor and a model.`
            : 'An agent is a role put to work: it combines a role with an executor, a model and a project.'
        }
        actions={
          <button
            type="button"
            className="btn-primary"
            onClick={() => setEditing('new')}
          >
            New agent
          </button>
        }
      />

      {pausedCount > 0 ? (
        <label className="mb-3 flex items-center gap-2 text-sm text-ink-2">
          <input type="checkbox" checked={showPaused} onChange={(e) => setShowPaused(e.target.checked)} />
          Show paused agents ({pausedCount})
        </label>
      ) : null}
      <div className="panel overflow-x-auto">
        {lookups.isLoading.agents ? (
          <LoadingRows />
        ) : allAgents.length === 0 ? (
          <EmptyState
            title="No agents yet"
            action={
              <button
                type="button"
                className="btn-secondary"
                onClick={() => setEditing('new')}
              >
                Create an agent
              </button>
            }
          >
            You’ll need a project and a connected executor first. Most setups
            start with one Developer agent per project.
          </EmptyState>
        ) : (
          <table className="data-table">
            <thead>
              <tr>
                <th>Agent</th>
                <th>Runs on</th>
                <th>Status</th>
                <th className="text-right">Success</th>
                <th className="text-right">Avg cost</th>
                <th className="text-right">Avg time</th>
              </tr>
            </thead>
            <tbody>
              {agents.map((agent) => (
                <tr
                  key={agent.id}
                  data-clickable
                  onClick={() => setEditing(agent)}
                >
                  <td>
                    <span className="flex items-center gap-3">
                      <RoleAvatar roleName={lookups.roleName(agent.roleId)} />
                      <span className="min-w-0">
                        <span className="block font-medium text-ink">
                          {agent.name}
                        </span>
                        <span className="block text-sm text-ink-3">
                          {projectId
                            ? lookups.roleName(agent.roleId)
                            : `${lookups.roleName(agent.roleId)} in ${lookups.projectName(agent.projectId)}`}
                        </span>
                      </span>
                    </span>
                  </td>
                  <td>
                    <span className="block text-ink">
                      {lookups.agentConnection(agent)}
                    </span>
                    <span className="block text-sm text-ink-3">
                      {agent.model}
                    </span>
                    <ExhaustionBadge
                      exhaustion={
                        agent.connectionExhaustion ??
                        lookups.connections.find(
                          (c) => c.id === agent.executorCredentialId
                        )
                      }
                    />
                  </td>
                  <td>
                    {agent.currentRunId ? (
                      <Link
                        to="/runs/$runId"
                        params={{ runId: agent.currentRunId }}
                        onClick={(e) => e.stopPropagation()}
                        className="hover:underline"
                      >
                        <StatusBadge status="running" label="Working" />
                      </Link>
                    ) : (
                      <StatusBadge
                        status={agent.isActive ? 'active' : 'inactive'}
                        label={agent.isActive ? 'Ready' : 'Paused'}
                      />
                    )}
                  </td>
                  <td className="text-right tabular-nums">
                    {agent.successRatePercent != null
                      ? `${agent.successRatePercent}%`
                      : '—'}
                  </td>
                  <td className="text-right tabular-nums">
                    {(() => {
                      const cost = formatRunCost(
                        { costCents: agent.avgCostCents, billing: agent.avgCostAllSubscription ? 'subscription' : 'api' },
                        lookups.runCostDisplay
                      )
                      return <span title={cost.tooltip}>{cost.text}</span>
                    })()}
                  </td>
                  <td className="text-right tabular-nums">
                    {formatMs(agent.avgDurationMs)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <Drawer
        open={editing != null}
        onClose={close}
        title={editing && editing !== 'new' ? editing.name : 'New agent'}
        description={
          editing === 'new'
            ? 'Pick the role it plays, where it works and what runs it.'
            : 'Changes apply to the next run.'
        }
      >
        {editing ? (
          <AgentForm
            key={editing === 'new' ? 'new' : editing.id}
            initial={editing === 'new' ? null : editing}
            lookups={lookups}
            fixedProjectId={projectId}
            isSubmitting={saveMutation.isPending}
            error={
              saveMutation.isError
                ? (saveMutation.error as Error).message
                : null
            }
            onCancel={close}
            onSubmit={(values) => saveMutation.mutate(values)}
          />
        ) : null}
        {editing && editing !== 'new' ? (
          <div className="mt-6 border-t border-line pt-4">
            <button
              type="button"
              className="btn-danger-quiet btn-sm"
              onClick={() => void handleDelete(editing)}
              disabled={removeMutation.isPending}
            >
              Delete agent
            </button>
            <p className="mt-1.5 text-sm text-ink-3">Only possible while it has no runs. Otherwise pause it above.</p>
          </div>
        ) : null}
      </Drawer>
    </div>
  )
}

interface AgentFormValues {
  name: string
  roleId: string
  projectId: string
  executorId: string
  /** The connection it runs with; its kind is `executorId`. */
  executorCredentialId: string | null
  /** Which CLI runs it, where the connection allows more than one; null = its default. */
  runner: string | null
  model: string
  additionalInstruction: string | null
  isActive: boolean
  /** Skills added or removed on top of the role's defaults. */
  skillOverrides: AgentSkillOverride[]
  /** Must stay a subset of the role's `allowedMcpServiceIds` (FD §14.2, narrowing only). */
  mcpServiceIds: string[]
  sandboxProfileId: string
  /** Dollars/minutes in the UI; converted to cents/ms right before submit. */
  maxCostDollars: string
  maxDurationMinutes: string
  /** Partial run settings over the role's (agent wins); null = the role's as they are. */
  runSettingsOverride: RunSettingsOverride | null
}

function Choice({
  id,
  value,
  onChange,
  options,
  placeholder,
}: {
  id: string
  value: string
  onChange: (value: string) => void
  options: { id: string; name: string }[]
  placeholder: string
}) {
  // Fall back to a raw id input when the list couldn't be loaded, so the form still works.
  if (options.length === 0) {
    return (
      <input
        id={id}
        className="field-input font-mono"
        placeholder={`${placeholder} id`}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        required
      />
    )
  }
  return (
    <select
      id={id}
      className="field-input"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      required
    >
      <option value="" disabled>
        {placeholder}
      </option>
      {options.map((o) => (
        <option key={o.id} value={o.id}>
          {o.name}
        </option>
      ))}
    </select>
  )
}

/** Pins one skill to an imported version; 'latest' follows new imports. */
function SkillVersionPicker({
  skillId,
  value,
  onChange,
}: {
  skillId: string
  value: string
  onChange: (version: string) => void
}) {
  const versionsQuery = useQuery({
    queryKey: ['skill-versions', skillId],
    queryFn: () => skillsApi.versions(skillId),
    staleTime: 60_000,
  })
  const versions = versionsQuery.data?.versions.map((v) => v.version) ?? []
  // Keep a pin the list doesn't (yet) show selectable, so it isn't silently dropped.
  const options = value !== 'latest' && !versions.includes(value) ? [value, ...versions] : versions
  return (
    <select
      aria-label="Skill version"
      className="field-input w-32 py-1 text-xs"
      value={value}
      onChange={(e) => onChange(e.target.value)}
    >
      <option value="latest">Latest</option>
      {options.map((version) => (
        <option key={version} value={version}>
          {version}
        </option>
      ))}
    </select>
  )
}

export function AgentForm({
  initial,
  lookups,
  fixedProjectId,
  isSubmitting,
  error,
  onCancel,
  onSubmit,
}: {
  initial: Agent | null
  lookups: ReturnType<typeof useLookups>
  /** Set inside a project: the agent belongs to it and the form doesn’t ask. */
  fixedProjectId?: string
  isSubmitting: boolean
  error: string | null
  onCancel: () => void
  onSubmit: (values: Partial<Agent>) => void
}) {
  const [form, setForm] = useState<AgentFormValues>({
    name: initial?.name ?? '',
    roleId: initial?.roleId ?? '',
    projectId:
      initial?.projectId ??
      fixedProjectId ??
      (lookups.projects.length === 1 ? lookups.projects[0].id : ''),
    executorId: initial?.executorId ?? '',
    executorCredentialId: initial?.executorCredentialId ?? null,
    runner: initial?.runner ?? null,
    model: initial?.model ?? '',
    additionalInstruction: initial?.additionalInstruction ?? null,
    isActive: initial?.isActive ?? true,
    skillOverrides: initial?.skillOverrides ?? [],
    mcpServiceIds: initial?.mcpServiceIds ?? [],
    sandboxProfileId: initial?.sandboxProfileId ?? 'ephemeral_container',
    maxCostDollars:
      initial?.budgetLimits?.maxCostCents != null
        ? String(initial.budgetLimits.maxCostCents / 100)
        : '',
    maxDurationMinutes:
      initial?.budgetLimits?.maxDurationMs != null
        ? String(Math.round(initial.budgetLimits.maxDurationMs / 60_000))
        : '',
    runSettingsOverride: initial?.runSettingsOverride ?? null,
  })
  const [validationError, setValidationError] = useState<string | null>(null)
  const connections = lookups.connections
  const selectedConnection = connections.find((c) => c.id === form.executorCredentialId) ?? null
  const formWorkspaceId = useWorkspaceId()

  // The role decides the baseline: its own skills/MCP services, narrowed or
  // extended by this agent (FD §14.2). Loaded per selected role so the
  // skills/MCP sections below can show what's already granted vs. added.
  const roleDetail = useQuery({
    queryKey: ['role-detail', formWorkspaceId, form.roleId],
    queryFn: () => rolesApi.get(form.roleId, formWorkspaceId),
    enabled: Boolean(formWorkspaceId && form.roleId),
    staleTime: 60_000,
  })
  // The role version the agent actually runs with: dispatch resolves
  // `agent.roleVersionId` (config-resolution.service.ts), which is set when
  // the agent is created (then the role's current version) and doesn't follow
  // later role edits. So an existing agent inherits from its own version; a
  // new agent (or one moved to another role) from the role's current one.
  const pinnedRoleVersion =
    initial && form.roleId === initial.roleId ? initial.roleVersion : null
  const needsPinnedVersion =
    pinnedRoleVersion != null &&
    roleDetail.data != null &&
    roleDetail.data.current.version !== pinnedRoleVersion
  const roleVersionsQuery = useQuery({
    queryKey: ['role-versions', formWorkspaceId, form.roleId],
    queryFn: () => rolesApi.versions(form.roleId, formWorkspaceId),
    enabled: Boolean(formWorkspaceId && form.roleId && needsPinnedVersion),
    staleTime: 60_000,
  })
  const effectiveRoleVersion = needsPinnedVersion
    ? (roleVersionsQuery.data?.versions.find((v) => v.version === pinnedRoleVersion) ??
      // The pinned version can't be found (shouldn't happen): fall back to current.
      (roleVersionsQuery.isSuccess ? roleDetail.data?.current : undefined))
    : roleDetail.data?.current
  const roleSkillIds = effectiveRoleVersion?.skillIds ?? []
  const roleMcpServiceIds = effectiveRoleVersion?.allowedMcpServiceIds ?? []
  // Parsed so a role version saved before a switch existed shows its default.
  const parsedRoleSettings = effectiveRoleVersion
    ? safeParseRoleRunSettings(effectiveRoleVersion.runSettings ?? undefined)
    : null
  const roleRunSettings = parsedRoleSettings?.success ? parsedRoleSettings.data : null

  const skillsQuery = useQuery({
    queryKey: ['skills', formWorkspaceId],
    queryFn: () => skillsApi.list({ workspaceId: formWorkspaceId }),
    enabled: Boolean(formWorkspaceId),
    staleTime: 60_000,
  })
  const mcpServicesQuery = useQuery({
    queryKey: ['mcp-services', formWorkspaceId],
    queryFn: () => mcpServicesApi.list({ workspaceId: formWorkspaceId }),
    enabled: Boolean(formWorkspaceId),
    staleTime: 60_000,
  })
  const skills = skillsQuery.data?.items ?? []
  const mcpServices = mcpServicesQuery.data?.items ?? []

  // An agent may only narrow its role: its sandbox must be at least as
  // isolated as the role's (SANDBOX_PROFILE_TYPES runs from least to most
  // isolated; the API's assertNarrowsRole rejects anything weaker).
  const roleSandboxIndex = Math.max(
    0,
    SANDBOX_PROFILE_TYPES.indexOf(roleDetail.data?.defaultSandboxProfile ?? 'none')
  )
  const sandboxOptions = SANDBOX_PROFILE_TYPES.slice(roleSandboxIndex)

  // Dropped MCP services no longer allowed by a newly-picked role. A new
  // agent takes the role's sandbox; an existing one keeps its own unless
  // that is weaker than the role's.
  useEffect(() => {
    if (!roleDetail.data || !effectiveRoleVersion) return
    const roleSandbox = roleDetail.data.defaultSandboxProfile
    setForm((f) => ({
      ...f,
      mcpServiceIds: f.mcpServiceIds.filter((id) => roleMcpServiceIds.includes(id)),
      sandboxProfileId:
        !initial ||
        SANDBOX_PROFILE_TYPES.indexOf(f.sandboxProfileId as SandboxProfileType) <
          SANDBOX_PROFILE_TYPES.indexOf(roleSandbox)
          ? roleSandbox
          : f.sandboxProfileId,
    }))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roleDetail.data, effectiveRoleVersion])

  function skillEnabled(skillId: string): boolean {
    const override = form.skillOverrides.find((o) => o.skillId === skillId)
    if (override) return override.enabled
    return roleSkillIds.includes(skillId)
  }

  function skillVersion(skillId: string): string {
    return form.skillOverrides.find((o) => o.skillId === skillId)?.skillVersion ?? 'latest'
  }

  // Only keep an override when it actually differs from the role's default:
  // other on/off state, or a pinned version ('latest' = not pinned).
  function setSkill(skillId: string, enabled: boolean, version = skillVersion(skillId)): void {
    const isDefault = roleSkillIds.includes(skillId)
    const rest = form.skillOverrides.filter((o) => o.skillId !== skillId)
    const pinned = enabled && version !== 'latest'
    setForm({
      ...form,
      skillOverrides:
        enabled !== isDefault || pinned
          ? [...rest, { skillId, skillVersion: pinned ? version : 'latest', enabled }]
          : rest,
    })
  }

  function toggleMcpService(id: string, checked: boolean): void {
    setForm({
      ...form,
      mcpServiceIds: checked
        ? [...form.mcpServiceIds, id]
        : form.mcpServiceIds.filter((existing) => existing !== id),
    })
  }

  function handleSubmit(event: FormEvent): void {
    event.preventDefault()
    const maxCostCents = form.maxCostDollars.trim()
      ? Math.round(Number(form.maxCostDollars) * 100)
      : undefined
    const maxDurationMs = form.maxDurationMinutes.trim()
      ? Math.round(Number(form.maxDurationMinutes) * 60_000)
      : undefined
    const payload: Partial<Agent> = {
      name: form.name,
      roleId: form.roleId,
      projectId: form.projectId,
      executorId: form.executorId,
      executorCredentialId: form.executorCredentialId,
      runner: form.runner,
      model: form.model,
      additionalInstruction: form.additionalInstruction,
      isActive: form.isActive,
      skillOverrides: form.skillOverrides,
      mcpServiceIds: form.mcpServiceIds,
      sandboxProfileId: form.sandboxProfileId,
      budgetLimits: {
        ...(maxCostCents != null ? { maxCostCents } : {}),
        ...(maxDurationMs != null ? { maxDurationMs } : {}),
      },
      // null clears a stored override; a new agent without one sends nothing.
      ...(initial || form.runSettingsOverride
        ? { runSettingsOverride: form.runSettingsOverride }
        : {}),
    }
    const result = AgentCreateSchema.safeParse({ workspaceId: formWorkspaceId, ...payload })
    if (!result.success) {
      setValidationError(result.error.issues[0]?.message ?? 'Invalid agent configuration')
      return
    }
    setValidationError(null)
    onSubmit(payload)
  }

  return (
    <form className="space-y-5" onSubmit={handleSubmit}>
      <Field
        label="Name"
        htmlFor="agent-name"
        hint="Shown in runs and approvals."
      >
        <input
          id="agent-name"
          className="field-input"
          placeholder="Goblic developer"
          value={form.name}
          onChange={(e) => setForm({ ...form, name: e.target.value })}
          required
        />
      </Field>
      <Field
        label="Role"
        htmlFor="agent-role"
        hint="Defines its instructions, skills and what it may do."
      >
        <Choice
          id="agent-role"
          value={form.roleId}
          onChange={(roleId) => setForm({ ...form, roleId })}
          options={lookups.roles.filter((r) => !r.isDeactivated)}
          placeholder="Choose a role"
        />
      </Field>
      {fixedProjectId ? null : (
      <Field label="Project" htmlFor="agent-project">
        <Choice
          id="agent-project"
          value={form.projectId}
          onChange={(projectId) => setForm({ ...form, projectId })}
          options={lookups.projects}
          placeholder="Choose a project"
        />
      </Field>
      )}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <Field
          label="Connection"
          htmlFor="agent-connection"
          hint={
            connections.length === 0 ? (
              <>
                No connections yet.{' '}
                <Link to="/executors" className="link">
                  Add one on the Executors page
                </Link>
                .
              </>
            ) : undefined
          }
        >
          <select
            id="agent-connection"
            className="field-input"
            value={form.executorCredentialId ?? ''}
            // Another connection has other models: back to its default.
            onChange={(event) => {
              const connection = connections.find(
                (c) => c.id === event.target.value
              )
              setForm({
                ...form,
                executorCredentialId: connection?.id ?? null,
                executorId: connection?.kind ?? form.executorId,
                model: '',
                runner: null,
              })
            }}
            required
          >
            <option value="" disabled>
              {form.executorCredentialId == null && form.executorId
                ? `Any ${CONNECTION_KIND_LABEL[form.executorId as ConnectionKind] ?? form.executorId} connection`
                : 'Choose a connection'}
            </option>
            {(Object.keys(CONNECTION_KIND_LABEL) as ConnectionKind[]).map(
              (kind) => {
                const ofKind = connections.filter((c) => c.kind === kind)
                return ofKind.length ? (
                  <optgroup key={kind} label={CONNECTION_KIND_LABEL[kind]}>
                    {ofKind.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.name}
                        {(() => {
                          const badge = exhaustionBadge(c)
                          return badge ? ` · ${badge.inline}` : ''
                        })()}
                      </option>
                    ))}
                  </optgroup>
                ) : null
              }
            )}
          </select>
        </Field>
        <Field label="Model" htmlFor="agent-model">
          <ModelPicker
            id="agent-model"
            workspaceId={formWorkspaceId}
            target={
              form.executorCredentialId
                ? { connectionId: form.executorCredentialId }
                : form.executorId
                  ? { kind: form.executorId }
                  : null
            }
            value={form.model}
            onChange={(model) => setForm({ ...form, model })}
          />
        </Field>
      </div>
      {selectedConnection && selectedConnection.allowedRunners.length > 1 ? (
        <Field
          label="Runs with"
          htmlFor="agent-runner"
          hint={`This connection can run this agent through more than one runner. Default: ${RUNNER_LABEL[selectedConnection.allowedRunners[0]] ?? selectedConnection.allowedRunners[0]}.`}
        >
          <select
            id="agent-runner"
            className="field-input"
            value={form.runner ?? ''}
            onChange={(e) => setForm({ ...form, runner: e.target.value || null })}
          >
            <option value="">
              Default ({RUNNER_LABEL[selectedConnection.allowedRunners[0]] ?? selectedConnection.allowedRunners[0]})
            </option>
            {selectedConnection.allowedRunners.map((runner) => (
              <option key={runner} value={runner}>
                {RUNNER_LABEL[runner] ?? runner}
              </option>
            ))}
          </select>
        </Field>
      ) : null}
      <Field
        label="Extra instruction"
        htmlFor="agent-instruction"
        hint="Added on top of the role’s instructions. Optional."
      >
        <textarea
          id="agent-instruction"
          className="field-input"
          rows={4}
          value={form.additionalInstruction ?? ''}
          onChange={(e) =>
            setForm({ ...form, additionalInstruction: e.target.value || null })
          }
        />
      </Field>
      <Field
        label="Skills"
        htmlFor="agent-skills"
        hint="On top of the role’s own skills. Untick one to disable it for just this agent."
      >
        <div id="agent-skills" className="space-y-2">
          {skillsQuery.isLoading ? (
            <LoadingRows />
          ) : skills.length === 0 ? (
            <p className="text-sm text-ink-3">No skills imported yet.</p>
          ) : (
            skills.map((skill) => {
              const enabled = skillEnabled(skill.id)
              return (
                <div key={skill.id} className="flex items-center gap-2.5">
                  <input
                    type="checkbox"
                    checked={enabled}
                    onChange={(e) => setSkill(skill.id, e.target.checked)}
                    className="h-4 w-4 accent-[rgb(var(--moss))]"
                  />
                  <span className="min-w-0 flex-1 text-sm text-ink">
                    {skill.name}
                    {roleSkillIds.includes(skill.id) ? (
                      <span className="text-ink-3"> · role default</span>
                    ) : null}
                  </span>
                  {enabled ? (
                    <SkillVersionPicker
                      skillId={skill.id}
                      value={skillVersion(skill.id)}
                      onChange={(version) => setSkill(skill.id, true, version)}
                    />
                  ) : null}
                </div>
              )
            })
          )}
        </div>
      </Field>
      <Field
        label="MCP servers"
        htmlFor="agent-mcp"
        hint="Tool servers this agent can use in its runs, limited to what the role allows."
      >
        <div id="agent-mcp" className="space-y-2">
          {mcpServicesQuery.isLoading ? (
            <LoadingRows />
          ) : roleMcpServiceIds.length === 0 ? (
            <p className="text-sm text-ink-3">
              {form.roleId
                ? 'This role doesn’t allow any MCP servers. Allow them on the role first.'
                : 'Choose a role first.'}
            </p>
          ) : (
            mcpServices
              .filter((service) => roleMcpServiceIds.includes(service.id))
              .map((service) => (
                <label key={service.id} className="flex items-center gap-2.5 text-sm text-ink">
                  <input
                    type="checkbox"
                    checked={form.mcpServiceIds.includes(service.id)}
                    onChange={(e) => toggleMcpService(service.id, e.target.checked)}
                    className="h-4 w-4 accent-[rgb(var(--moss))]"
                  />
                  {service.name}
                </label>
              ))
          )}
        </div>
      </Field>
      <Field label="Sandbox profile" htmlFor="agent-sandbox">
        <select
          id="agent-sandbox"
          className="field-input"
          value={form.sandboxProfileId}
          onChange={(e) => setForm({ ...form, sandboxProfileId: e.target.value })}
        >
          {sandboxOptions.map((type) => (
            <option key={type} value={type}>
              {SANDBOX_PROFILES[type].label}
            </option>
          ))}
        </select>
      </Field>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <Field
          label="Max cost per run"
          htmlFor="agent-max-cost"
          hint="In dollars. Leave empty for the role’s default."
        >
          <input
            id="agent-max-cost"
            type="number"
            min="0"
            step="0.01"
            className="field-input"
            placeholder="No limit"
            value={form.maxCostDollars}
            onChange={(e) => setForm({ ...form, maxCostDollars: e.target.value })}
          />
        </Field>
        <Field
          label="Max duration per run"
          htmlFor="agent-max-duration"
          hint="In minutes. Leave empty for the role’s default."
        >
          <input
            id="agent-max-duration"
            type="number"
            min="0"
            step="1"
            className="field-input"
            placeholder="No limit"
            value={form.maxDurationMinutes}
            onChange={(e) => setForm({ ...form, maxDurationMinutes: e.target.value })}
          />
        </Field>
      </div>
      <RunSettingsOverrides
        value={form.runSettingsOverride}
        onChange={(runSettingsOverride) => setForm({ ...form, runSettingsOverride })}
        roleSettings={roleRunSettings}
        roleName={form.roleId ? lookups.roleName(form.roleId) : null}
      />
      <label className="flex items-center gap-2.5 text-base text-ink">
        <input
          type="checkbox"
          checked={form.isActive}
          onChange={(e) => setForm({ ...form, isActive: e.target.checked })}
          className="h-4 w-4 accent-[rgb(var(--moss))]"
        />
        Can take new tasks
      </label>

      {validationError ? <Notice tone="error">{validationError}</Notice> : null}
      {error ? <Notice tone="error">{error}</Notice> : null}

      <div className="flex justify-end gap-2 border-t border-line pt-4">
        <button type="button" className="btn-ghost" onClick={onCancel}>
          Cancel
        </button>
        <button type="submit" className="btn-primary" disabled={isSubmitting}>
          {isSubmitting ? 'Saving…' : initial ? 'Save changes' : 'Create agent'}
        </button>
      </div>
    </form>
  )
}
